import {
  appendElementOffscreen,
  debugDimSimulatedElement,
  debugLog,
  forceElementWidth,
  getElement,
  isHidden,
} from "./utils";
import {
  BARCODE_CONTAINER,
  BIOENGINEERED,
  BUSINESS_INFO_CONTAINER,
  BUSINESS_INFO_TEXTAREA,
  FACILITY_ALLERGEN_LIST,
  INGREDIENTS_AND_ALLERGENS,
  NUTRITION_FACTS_PANEL,
  RECIPE_TITLE,
  LABEL_LOGO_WRAPPER,
} from "./constants";

/**
 * Measure the label preview.
 *
 * Gets width from Nutrition Facts, then sums heights of all displayed pieces of the label.
 * 1) measure actual Nutrition Facts panel to get width and height
 * 2) measure the logo if visible
 * 3) simulate print style for recipe title
 * 4) measure actual ingredient list and allergens
 * 5) simulate print style for business info
 * 6) measure actual bioengineered claim
 * 7) measure actual facility allergens list
 *    (for US formats - in Canadian formats, facility allergens are part of ingredient-and-allergens)
 * 8) measure the barcode container if visible
 *
 * Return final {widthPx, heightPx}.
 */
export const measureLabelPreview = () => {
  debugLog("[measureLabelInches] Starting measurement...");

  const nutritionFactsBox = measureNutritionFacts();
  if (nutritionFactsBox.width <= 0) {
    throw new Error("Nutrition Facts has zero width, can't measure label properly.");
  }

  const maxWidth = nutritionFactsBox.width;
  const logoHeight = measureLogoHeight();
  const recipeTitleHeight = measureRecipeTitleHeight({ maxWidth });
  const ingredientsAndAllergensHeight = measureIngredientsAndAllergensHeight();
  const bioEngineeredHeight = measureBioEngineeredHeight();
  const usFacilityAllergensHeight = measureUSFacilityAllergensHeight();
  const barcodeHeight = measureBarcodeHeight();

  const businessInfoHeight = measureBusinessInfoHeight({
    maxWidth,
    isLastItem: !usFacilityAllergensHeight && !bioEngineeredHeight && !barcodeHeight,
  });

  const totalHeight =
    nutritionFactsBox.height +
    logoHeight +
    recipeTitleHeight +
    businessInfoHeight +
    ingredientsAndAllergensHeight +
    bioEngineeredHeight +
    usFacilityAllergensHeight +
    barcodeHeight;

  // The hardcoded +8 accounts for `tool.border(4 * zoom)` in the render service (the `4` on both sides, not the `* zoom` part)
  const finalWidth = nutritionFactsBox.width + 8;
  const finalHeight = totalHeight + 8;

  if (window?.DEBUG) {
    // This is guarded this with window.DEBUG because there's no need to JSON.stringify if debug logging is disabled.
    debugLog(
      "[measureLabelInches] total => width:",
      finalWidth,
      " height:",
      finalHeight,
      `(height is based on summing: ${JSON.stringify({
        logoHeight,
        recipeTitleHeight,
        "nutritionFactsHeight": nutritionFactsBox.height,
        ingredientsAndAllergensHeight,
        businessInfoHeight,
        bioEngineeredHeight,
        usFacilityAllergensHeight,
        barcodeHeight,
        "hardcoded vertical padding": 8,
      })})`
    );
  }

  return {
    widthPx: finalWidth,
    heightPx: finalHeight,
  };
};

/// == The pieces that can be directly measured in a straightforward way == ///

export const measureBioEngineeredHeight = () => measureVisibleElementHeight(BIOENGINEERED);

export const measureUSFacilityAllergensHeight = () =>
  measureVisibleElementHeight(FACILITY_ALLERGEN_LIST);

export const measureBarcodeHeight = () => measureVisibleElementHeight(BARCODE_CONTAINER);

export const measureLogoHeight = () => {
  const logoWrapper = document.querySelector(LABEL_LOGO_WRAPPER);
  if (logoWrapper && !isHidden(logoWrapper)) {
    const rect = logoWrapper.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(logoWrapper);
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
    return rect.height + marginBottom;
  }
  return 0;
};

export const measureIngredientsAndAllergensHeight = () => {
  for (const selectors of INGREDIENTS_AND_ALLERGENS) {
    let total = 0;
    for (const sel of selectors) {
      total += measureVisibleElementHeight(sel, { throwIfNotFound: false });
    }
    if (total > 0) {
      return total;
    }
  }
  return 0;
};

/* Return the boundingRect height in px for a normal element (or 0 if hidden). */
export function measureVisibleElementHeight(selector, { throwIfNotFound = false } = {}) {
  const el = throwIfNotFound ? getElement(selector) : document.querySelector(selector);
  if (!el || isHidden(el) || !el.innerText) {
    return 0;
  }
  const rect = el.getBoundingClientRect();
  return rect.height;
}

/**
 * Measure the Nutrition Facts box. If it's hidden, returns height of 0.
 * (We always need to measure its width.)
 */
export const measureNutritionFacts = () => {
  const nutritionFactsBoxEl = getElement(NUTRITION_FACTS_PANEL);
  const rect = nutritionFactsBoxEl.getBoundingClientRect();
  const width = rect.width;
  const height = isHidden(nutritionFactsBoxEl) ? 0 : rect.height;
  debugLog("[measureNutritionFacts] =>", { width, height });
  return { width, height };
};

/// == The pieces that are more "special," in one way or another == ///

/**
 * Return the boundingRect height in px for the #recipe-title in its "print" style.
 * If it's hidden, we do 0. If visible, we build an offscreen block with the print style.
 * The actual print style is defined in print.scss, and this should match that.
 */
export const measureRecipeTitleHeight = ({ maxWidth }) => {
  const debugElementId = "debug-recipe-title";
  const titleEl = getElement(RECIPE_TITLE);
  if (
    !titleEl ||
    isHidden(titleEl) ||
    (titleEl && titleEl.classList.contains("recipe-title-hidden"))
  ) {
    debugDimSimulatedElement(debugElementId);
    debugLog("[measureRecipeTitleHeight] Recipe title is hidden. => 0");
    return 0;
  }

  // We'll mimic the print style for #recipe-title. We do not want to
  // measure the actual in-page style, as it differs significantly.
  const dummy = document.createElement("div");

  // `title` class mainly applies `word-break: break-word` and bold;
  // we override the fonts and such to match print.scss.
  dummy.className = "title";
  dummy.style.fontFamily = '"Lato", sans-serif';
  dummy.style.fontSize = "22px";
  dummy.style.lineHeight = "30px";
  dummy.style.paddingBottom = "0.75em"; // Padding instead of margin.

  forceElementWidth(dummy, maxWidth);
  dummy.textContent = titleEl.textContent;

  appendElementOffscreen({ element: dummy, debugElementId });
  const rect = dummy.getBoundingClientRect();
  debugLog("[measureRecipeTitleHeight] =>", rect.height);
  document.body.removeChild(dummy);
  return rect.height;
};

/**
 * Return the boundingRect height in px for the "business info" block,
 * containing the text from the text area. We copy the relevant styling such as
 * font size, as well as hardcode some styling to match #recipe_manufactured_at in print.scss.
 */
export const measureBusinessInfoHeight = ({ maxWidth, isLastItem }) => {
  const debugElementId = "debug-manufacture-address";
  const visibilityEl = getElement(BUSINESS_INFO_CONTAINER);
  if (!visibilityEl || isHidden(visibilityEl)) {
    debugDimSimulatedElement(debugElementId);
    debugLog("[measureBusinessInfoHeight] Business info is hidden. => 0");
    return 0;
  }

  const referenceTextArea = getElement(BUSINESS_INFO_TEXTAREA);
  if (!referenceTextArea) return 0;
  let addressText = referenceTextArea.value;
  if (!addressText?.trim()) return 0;

  // Label auto-cropping will cut off the excess whitespace if business info is the last item.
  // If business info is not the last item, we need to keep more whitespace.
  if (isLastItem) {
    addressText = addressText.trim();
  }

  // We imitate the div#manufacture-address > div#recipe_manufactured_at_growing_textarea structure,
  // and we'll apply styles similar to print.scss.
  const dummy = document.createElement("div");
  dummy.className = "pt-4"; // pt-4,pb-4 are based on my-4 in _show.html.erb.
  if (!isLastItem) {
    dummy.className = `${dummy.className} pb-4`;
  }

  const dummyTextArea = document.createElement("textarea");
  dummy.appendChild(dummyTextArea);

  // ".textarea" is based on _show.html.erb.
  dummyTextArea.className = "textarea";

  // In _show.html.erb the textarea has `rows` = 2, which effectively sets a minimum height.
  // To set the actual height, it's not quite accurate to copy the height from the growing-text-area,
  // but it works out to set `rows` based on linebreaks in the text content.
  dummyTextArea.rows = Math.max(2, addressText.split("\n").length);

  let referenceStyle = window.getComputedStyle(referenceTextArea);
  dummyTextArea.style.fontSize = referenceStyle.fontSize;
  dummyTextArea.style.lineHeight = referenceStyle.lineHeight;
  dummyTextArea.style.textAlign = referenceStyle.textAlign;
  dummyTextArea.style.textTransform = referenceStyle.textTransform;

  dummyTextArea.style.minWidth = "unset";
  dummyTextArea.style.boxSizing = "content-box";
  dummyTextArea.style.whiteSpace = "pre-wrap";
  dummyTextArea.style.overflowWrap = "break-word";
  dummyTextArea.style.padding = "0";

  // These are intended to match print.scss
  dummy.style.paddingBottom = "1px"; // Padding on container, instead of margin on textarea itself
  dummyTextArea.style.border = "none";
  dummyTextArea.style.boxShadow = "none";
  dummyTextArea.style.resize = "none";

  forceElementWidth(dummy, maxWidth);
  dummyTextArea.value = addressText;

  appendElementOffscreen({ element: dummy, debugElementId });
  const rect = dummy.getBoundingClientRect();
  document.body.removeChild(dummy);

  return rect.height;
};
